/* eslint-disable standard/computed-property-even-spacing */
import {
  VuexModule,
  Module,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import { IAuthState, IJWTPayload } from '@/store/types'
import Vue from 'vue'
import { ResponseStatus } from '@/api'
import {
  closeRealtimeConnection,
  reconnectRealtimeConnection
} from '@/utils/realtime'
import store from '@/store'
import sharedStore from '@/modules/shared/shared.store'
import jwt from 'jsonwebtoken'
import moment from 'moment'

import { MemberAPI, MemberEntity } from '@/api/member'
import { IpcRendererEvent } from 'electron'
import { isElectron } from '@/utils/electron'
import { VuexOidcStoreActions, VuexOidcStoreGetters } from 'vuex-oidc'
import LiveChatAPI from '@/utils/livechat'
import { AccountInfo, IdentityAPI } from '@/api/identity'
import router from '@/router'

@Module({ dynamic: true, store: store, namespaced: true, name: 'user' })
export default class AuthModule extends VuexModule implements IAuthState {
  authToken: string | null = null
  member: MemberEntity | null = null
  accountInfo: AccountInfo | null = null
  refreshTokenEvent: EventTarget = new EventTarget()
  isRefreshingToken: boolean = false
  isNewMember: boolean = false
  isLoading: boolean = false

  @Mutation
  SET_AUTH_TOKEN(authToken: string) {
    this.authToken = authToken

    if (!isElectron()) {
      localStorage.setItem('access_token', this.authToken!)
    }

    if (!authToken) {
      reconnectRealtimeConnection().then()
    }
  }

  @Mutation
  SET_MEMBER(member: MemberEntity) {
    this.member = member
  }

  @Mutation
  SET_ACCOUNT_INFO(accountInfo: AccountInfo) {
    this.accountInfo = accountInfo
  }

  @Mutation
  SET_IS_NEW_MEMBER(value: boolean) {
    this.isNewMember = value
  }

  @Mutation
  SET_IS_LOADING(value: boolean) {
    this.isLoading = value
  }

  @Mutation
  SET_REFRESHING_TOKEN(isRefreshingToken: boolean) {
    this.isRefreshingToken = isRefreshingToken

    if (!isRefreshingToken) {
      this.refreshTokenEvent.dispatchEvent(new Event('finish'))
    }
  }

  @Action({ rawError: true })
  async setAuthToken(authToken: string) {
    this.context.commit('SET_AUTH_TOKEN', authToken)
    return true
  }

  @Action({ rawError: true })
  requestTokenRefresh() {
    if (isElectron()) {
      return new Promise(async (resolve, reject) => {
        if (this.hasLoggedIn) {
          // check if we are refreshing token
          if (this.isRefreshingToken) {
            this.refreshTokenEvent.addEventListener('finish', resolve, {
              once: true
            })
          } else {
            // do the token refresh job
            const refreshToken = async () => {
              this.context.commit('SET_REFRESHING_TOKEN', true)
              await this.requestNewToken()
              resolve(true)
              this.context.commit('SET_REFRESHING_TOKEN', false)
            }

            await refreshToken()
          }
        } else {
          // token not exists, thrown error.
          reject(new Error('No refresh token'))
        }
      })
    } else {
      return new Promise(async (resolve, reject) => {
        if (this.hasLoggedIn) {
          this.context.commit('SET_REFRESHING_TOKEN', false)
        }
      })
    }
  }

  // Brian: Hey! DO NOT CALL THIS ACTION UNLESS YOU KNOW WHAT YOU ARE DOING
  // Use fetchMember() instead. This will fuck up the SignalR existing listeners as it will destroy old connection
  // Use this only before anything is initialized
  @Action({ rawError: true })
  async updateLoginState() {
    const sharedModule = getModule(sharedStore, store)
    sharedModule.stopRender()

    try {
      if (!this.hasLoggedIn) {
        // try to get token
        await this.requestNewToken()

        // still not logged in
        if (!this.hasLoggedIn) {
          LiveChatAPI.setup()
          return true
        }
      }

      if (!(await this.fetchMember())) {
        console.log('Fetch member is false')
        return false
      }

      try {
        await this.fetchAccountInfo()
      } catch (err) {
        console.log('Unable to fetch account. ', err)
      }

      let result = jwt.decode(this.authToken!) as IJWTPayload
      if (result) {
        let currentTime = moment().unix()
        if (currentTime >= result.exp) {
          await this.requestTokenRefresh()
        }
      }

      await reconnectRealtimeConnection()
    } finally {
      sharedModule.enableRender()
    }

    return true
  }

  @Action({ rawError: true })
  async fetchMember() {
    try {
      const res = await MemberAPI.getMember()
      const payload = res.data.payload
      if (!payload)
        throw new Error(
          'Account logged out - Unable to obtain member session info'
        )

      this.context.commit('SET_MEMBER', payload)
      this.context.commit('SET_IS_NEW_MEMBER', !!payload.isNewMember)

      LiveChatAPI.setup()
    } catch (err) {
      this.context.commit('SET_MEMBER', null)
      this.context.commit('SET_AUTH_TOKEN', null)
      return false
    }

    return true
  }

  @Action({ rawError: true })
  async fetchAccountInfo() {
    try {
      const res = await IdentityAPI.getAccount()
      const payload = res.data.payload
      if (!payload)
        throw new Error('Account logged out - Unable to obtain account info')

      this.context.commit('SET_ACCOUNT_INFO', payload)
    } catch (err) {
      this.context.commit('SET_ACCOUNT_INFO', null)
      return false
    }

    return true
  }

  @Action({ rawError: true })
  signIn() {
    console.log('Login detected, isElectron:', isElectron())

    if (isElectron()) {
      console.log('Attempting sign in with electron...')
      return new Promise((resolve, reject) => {
        this.context.commit('SET_IS_LOADING', true)
        window.ipcRenderer.send('auth-request')
        window.ipcRenderer.once(
          'auth-response',
          (event: IpcRendererEvent, data: { status: string }) => {
            // delay a bit because we need to refresh token
            setTimeout(() => {
              this.context.commit('SET_IS_LOADING', false)
            }, 500)

            if (data.status && data.status === 'ok') {
              resolve(true)
            } else {
              // Vue.prototype.$toast.error('Sign in failed.')
              reject(new Error('status is ' + data.status))
            }
          }
        )
      })
    } else {
      console.log('Attempting sign in with web oidc client...')
      localStorage.setItem('signin.return_path', router.currentRoute.fullPath)
      return store.dispatch('oidc/authenticateOidc')
    }
  }

  @Action({ rawError: true })
  async signOut() {
    // destroy livechat session
    LiveChatAPI.hide()
    if (isElectron()) {
      return new Promise((resolve, reject) => {
        window.ipcRenderer.send('sign-out-request')
        window.ipcRenderer.once(
          'sign-out-response',
          (e: IpcRendererEvent, data: { status: string }) => {
            if (data.status === 'ok') {
              this.context.commit('SET_AUTH_TOKEN', null)
              this.context.commit('SET_MEMBER', null)
              this.context.commit('SET_IS_NEW_MEMBER', false)
            }
          }
        )
      })
    } else {
      this.context.commit('SET_AUTH_TOKEN', null)
      this.context.commit('SET_MEMBER', null)
      this.context.commit('SET_IS_NEW_MEMBER', false)
      await store.dispatch('oidc/signOutOidcSilent')

      const sharedModule = getModule(sharedStore, store)
      sharedModule.rerender()
    }
  }

  @Action({ rawError: true })
  requestNewToken() {
    if (isElectron()) {
      return new Promise(resolve => {
        console.log('[ELECTRON] Requesting access token.')
        if (!window.ipcRenderer) {
          resolve(false)
          return
        }

        window.ipcRenderer.send('new-token-request')
        window.ipcRenderer.once('new-token-response', (event, args) => {
          console.log('New token received. status is', args.status)
          if (args.accessToken) {
            this.context.commit('SET_AUTH_TOKEN', args.accessToken)
          } else {
            this.context.commit('SET_AUTH_TOKEN', null)
          }
          resolve(true)
        })
      })
    } else {
      console.log('[WEB] Requesting access token.')
      return new Promise(resolve => {
        var token = this.oidcAccessToken
        this.context.commit('SET_AUTH_TOKEN', token || null)
        resolve(true)
      })
    }
  }

  get getAuthToken() {
    return this.authToken
  }

  get hasLoggedIn() {
    return !!this.authToken
  }

  get memberInfo() {
    return this.member
  }

  get userId() {
    return this.memberId
  }

  get memberId() {
    if (!this.member) {
      return ''
    }

    return this.member.memberId
  }

  get email() {
    if (!this.member) {
      return ''
    }

    return this.member.contactEmail
  }

  get nickname() {
    if (!this.member) {
      return ''
    }

    return this.member.nickname
  }

  // OpenID Connect Getters
  get oidcAccessToken() {
    return this.context.rootGetters[
      'oidc/oidcAccessToken'
    ] as VuexOidcStoreGetters['oidcAccessToken']
  }

  get oidcAuthenticationIsChecked() {
    return this.context.rootGetters[
      'oidc/oidcAuthenticationIsChecked'
    ] as VuexOidcStoreGetters['oidcAuthenticationIsChecked']
  }

  get oidcError() {
    return this.context.rootGetters[
      'oidc/oidcError'
    ] as VuexOidcStoreGetters['oidcError']
  }

  get oidcIdToken() {
    return this.context.rootGetters[
      'oidc/oidcIdToken'
    ] as VuexOidcStoreGetters['oidcIdToken']
  }

  get oidcIdTokenExp() {
    return this.context.rootGetters[
      'oidc/oidcIdTokenExp'
    ] as VuexOidcStoreGetters['oidcIdTokenExp']
  }

  get oidcIsAuthenticated() {
    return this.context.rootGetters[
      'oidc/oidcIsAuthenticated'
    ] as VuexOidcStoreGetters['oidcIsAuthenticated']
  }

  async oidcIsRoutePublic(route: any) {
    return this.context.rootGetters[
      'oidc/oidcIsRoutePublic'
    ] as VuexOidcStoreGetters['oidcIsRoutePublic']
  }

  get oidcRefreshToken(): string | null {
    return this.context.rootGetters['oidc/oidcRefreshToken']
  }

  get oidcRefreshTokenExp(): number | null {
    return this.context.rootGetters['oidc/oidcRefreshTokenExp']
  }

  get oidcScopes() {
    return this.context.rootGetters[
      'oidc/oidcScopes'
    ] as VuexOidcStoreGetters['oidcScopes']
  }

  get oidcUser() {
    return this.context.rootGetters[
      'oidc/oidcUser'
    ] as VuexOidcStoreGetters['oidcUser']
  }

  // OpenID Connect Getters

  /**
   * Handles callback from authentication redirect. Has an optional url parameter
   */
  @Action({ rawError: true })
  async oidcSignInCallback() {
    return (await store.dispatch(
      'oidc/oidcSignInCallback'
    )) as VuexOidcStoreActions['oidcSignInCallback']
  }

  /**
   * Authenticates if signed in. No redirect is made if not signed in
   */
  @Action({ rawError: true })
  async authenticateOidcSilent() {
    return (await store.dispatch(
      'oidc/authenticateOidcSilent'
    )) as VuexOidcStoreActions['authenticateOidcSilent']
  }

  /**
   * Get user from oidc-client storage and update it in vuex store. Returns a promise
   */
  @Action({ rawError: true })
  async getOidcUser() {
    return (await store.dispatch(
      'oidc/getOidcUser'
    )) as VuexOidcStoreActions['getOidcUser']
  }

  /**
   * Signs out user in open id provider
   */
  @Action({ rawError: true })
  async signOutOidc() {
    return (await store.dispatch(
      'oidc/signOutOidc'
    )) as VuexOidcStoreActions['signOutOidc']
  }

  /**
   * Signs out user in open id provider using a hidden iframe
   */
  @Action({ rawError: true })
  async signOutOidcSilent() {
    return (await store.dispatch(
      'oidc/signOutOidcSilent'
    )) as VuexOidcStoreActions['signOutOidcSilent']
  }

  /**
   * Signs out user in vuex and browser storage, but not in open id provider
   */
  @Action({ rawError: true })
  async removeOidcUser() {
    return (await store.dispatch(
      'oidc/authenticateOidc'
    )) as VuexOidcStoreActions['removeOidcUser']
  }

  /**
   * Authenticates with redirect to sign in if not signed in
   */
  @Action({ rawError: true })
  async authenticateOidc(redirectPath: string | undefined = undefined) {
    console.log('Invoking authenticateOidc with redirectPath:', redirectPath)
    return (await store.dispatch(
      'oidc/authenticateOidc',
      redirectPath
    )) as VuexOidcStoreActions['authenticateOidc']
  }

  // OpenID Connect Actions
}
